import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import { Link } from "react-router-dom";
import { Button, Icon, Input } from "@/ds";
import api from "@/lib/api";
import { clearAcquisition, getAcquisition } from "@/lib/acquisition";
import { useAuth } from "@/contexts/AuthContext";
import { anonymousKeyFor } from "@/lib/dany";
import DanyStageField from "@/components/dany/DanyStageField";

/**
 * Inline registration, one field at a time (DANY.md §2.3, redesigned in DY2.3).
 *
 * This is the old `InlineSignup` from `DanyJobDraftWidget` with its mechanism
 * kept whole and its presentation rebuilt. What did not change, because it is
 * what works and one half of it was paid for in production:
 *
 *   * Google first. It comes back with the address already verified, so the job
 *     publishes in the same breath; email and password are one step longer and
 *     the copy says so instead of hiding it.
 *   * **The anonymous key is handed to `onRegistered`, never deleted here.**
 *     Claiming the thread happens inside the publish call that follows, so
 *     until it returns the key in the browser is the only thing tying the new
 *     account to the conversation the proposal came from. `62a9238` deleted it
 *     one line early and every inline registration in production answered
 *     `404 action_not_found`.
 *   * `signupEmail(email, password, name, { anonymousKey })`, so the backend can
 *     see a job draft is waiting behind the verification link and send the mail
 *     that says so.
 *
 * What changed is the shape: three inputs stacked in one form became one
 * question per screen. On a phone the old form put the password field under the
 * keyboard the moment the name field was focused, and the person scrolled a
 * chat transcript looking for it.
 *
 * **The phone number is deliberately not here** (closed decision, DY2.3): a
 * client is never asked for one at registration. It belongs to the moment they
 * accept a quote, which is designed and not built.
 */

/**
 * The social buttons, declared rather than hard-coded.
 *
 * Facebook is in the list and `enabled: false`, which renders nothing at all —
 * Jorge, 2026-08-29, and the reasoning is that a dead button is worse than no
 * button. Turning it on is this flag plus a handler; the layout, the divider
 * and the copy slot already account for a second row.
 */
const OAUTH_PROVIDERS = [
  { id: "google", enabled: true, icon: "user", labelKey: "dany.signup.google" },
  { id: "facebook", enabled: false, icon: "user", labelKey: "dany.signup.facebook" },
];

const STEPS = ["name", "email", "password"];

export default function DanySignupCard({ threadId, busy, setBusy, onRegistered }) {
  const { t, i18n } = useTranslation();
  const { setUser, signupEmail } = useAuth();
  const [step, setStep] = React.useState("choose");
  // N3. **Which field they came back to fix, if they came back.**
  //
  // The confirmation card showed name, email and password as three lines of
  // text with nothing to press on them. The values were correct and the way to
  // change one was to walk the wizard backwards — Back, Back, Back — and then
  // forward again through the two steps that were already right. Nobody found
  // it, and the one field people want to fix at that moment is the email,
  // which is the furthest away.
  //
  // So a row is a button, and it goes straight to its step. `editing` is what
  // makes the return trip one tap too: while it is set, finishing a step goes
  // back to the confirmation instead of walking on to the next one.
  //
  // The job draft behind this card is untouched by any of it — it lives in
  // `DanyJobDraftWidget`, which renders this one inside itself, so stepping
  // around in here cannot lose it.
  const [editing, setEditing] = React.useState(null);
  const [values, setValues] = React.useState({ name: "", email: "", password: "" });
  const [error, setError] = React.useState(null);

  const anonymousKey = anonymousKeyFor(threadId);
  const set = (key) => (event) =>
    setValues((v) => ({ ...v, [key]: event.target.value }));

  const google = useGoogleLogin({
    onSuccess: async (response) => {
      setBusy(true);
      setError(null);
      try {
        const acquisition = getAcquisition();
        const { data } = await api.post("/auth/google", {
          access_token: response.access_token,
          // T22. Used by the server only if this call creates the account.
          ...(acquisition ? { acquisition } : {}),
          language: String(i18n.language || "en").slice(0, 2),
          // Someone drafting a job post is the requester: a client account.
          // A FixyBee signing in here already has a role, and keeps it.
          role: "client",
        });
        clearAcquisition();
        setUser(data.user);
        await onRegistered({ anonymousKey });
      } catch {
        setError(t("dany.signup.failed"));
      } finally {
        setBusy(false);
      }
    },
    onError: () => setError(t("dany.signup.failed")),
  });

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await signupEmail(
        values.email.trim(), values.password, values.name.trim(),
        { anonymousKey, role: "client" },
      );
      await onRegistered({
        anonymousKey, verificationSent: data.verification_sent,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "Email already registered") {
        setError(t("dany.signup.emailTaken"));
        setStep("email");            // back to the field that is wrong, not to the start
      } else {
        setError(
          (typeof detail === "string" && detail) || t("dany.signup.failed"));
        setStep("confirm");
      }
    } finally {
      setBusy(false);
    }
  };

  // Validated per step so nobody reaches the confirmation card with a value the
  // server will reject. The password rule is CLAUDE.md §5's, stated in the hint
  // rather than discovered by being refused.
  const problem = React.useMemo(() => {
    if (step === "name" && values.name.trim().length < 2) return "name";
    if (step === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim())) {
      return "email";
    }
    if (step === "password") {
      const p = values.password;
      if (p.length < 8 || !/[a-z]/.test(p) || !/[A-Z]/.test(p) || !/\d/.test(p)) {
        return "password";
      }
    }
    return null;
  }, [step, values]);

  const advance = (event) => {
    event?.preventDefault();
    if (problem) return;
    const index = STEPS.indexOf(step);
    setError(null);
    if (editing) {
      setEditing(null);
      setStep("confirm");
      return;
    }
    setStep(index === STEPS.length - 1 ? "confirm" : STEPS[index + 1]);
  };

  const back = () => {
    const index = STEPS.indexOf(step);
    setError(null);
    // Abandoning an edit leaves the value as it was on screen — it was typed
    // into the same state the summary reads, so there is nothing to revert and
    // nothing to lose.
    if (editing) {
      setEditing(null);
      setStep("confirm");
      return;
    }
    if (step === "confirm") setStep("password");
    else if (index === 0) setStep("choose");
    else setStep(STEPS[index - 1]);
  };

  /** Jump from the confirmation straight to one field, and straight back. */
  const edit = (field) => {
    setError(null);
    setEditing(field);
    setStep(field);
  };

  const shell = {
    marginTop: "var(--space-4)", paddingTop: "var(--space-4)",
    borderTop: "1px solid var(--border-subtle)",
  };

  const legal = (
    <p style={{
      marginTop: "var(--space-3)", fontSize: "var(--text-xs)",
      color: "var(--text-subtle)", lineHeight: "var(--leading-relaxed)",
    }}>
      <Trans i18nKey="dany.signup.legal">
        By continuing you accept the <Link to="/terms">Terms</Link> and the
        <Link to="/privacy">Privacy Policy</Link>.
      </Trans>
    </p>
  );

  if (step === "choose") {
    return (
      <div style={shell} data-testid="dany-signup-choose">
        <p style={header}>{t("dany.signup.header")}</p>
        <p style={sub}>{t("dany.signup.body")}</p>

        <div style={{
          marginTop: "var(--space-3)", display: "flex",
          flexDirection: "column", gap: "var(--space-2)",
        }}>
          {OAUTH_PROVIDERS.filter((p) => p.enabled).map((provider) => (
            <Button
              key={provider.id} variant="primary" size="md" icon={provider.icon}
              disabled={busy} onClick={() => google()}
              data-testid={`dany-signup-${provider.id}`}
            >{t(provider.labelKey)}</Button>
          ))}
        </div>

        <p style={{
          margin: "var(--space-3) 0", fontSize: "var(--text-xs)",
          color: "var(--text-subtle)", textAlign: "center",
        }}>{t("dany.signup.orEmail")}</p>

        <Button
          variant="ghost" size="md" icon="mail" disabled={busy}
          onClick={() => setStep("name")} data-testid="dany-signup-manual"
        >{t("dany.signup.withEmail")}</Button>

        {error && <p style={danger} data-testid="dany-signup-error">{error}</p>}
        {legal}
        <p style={{ marginTop: 6, fontSize: "var(--text-xs)", color: "var(--text-subtle)" }}>
          {t("dany.signup.haveAccount")}{" "}
          <Link to="/login" style={{ color: "var(--text-accent)" }}>
            {t("dany.signup.signIn")}
          </Link>
        </p>
      </div>
    );
  }

  if (step === "confirm") {
    return (
      <div style={shell} data-testid="dany-signup-confirm">
        <p style={header}>{t("dany.signup.confirmHeader")}</p>
        <p style={sub}>{t("dany.signup.confirmBody")}</p>
        <dl style={{ marginTop: "var(--space-3)" }}>
          {STEPS.map((field) => (
            <Summary
              key={field}
              label={t(`dany.signup.${field}`)}
              value={field === "password"
                ? "•".repeat(Math.min(values.password.length, 12))
                : values[field]}
              onEdit={() => edit(field)}
              editLabel={t("dany.signup.edit")}
              testid={`dany-signup-edit-${field}`}
            />
          ))}
        </dl>
        {error && <p style={danger} data-testid="dany-signup-error">{error}</p>}
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <Button
            variant="primary" size="md" iconRight="arrow-right" disabled={busy}
            onClick={create} data-testid="dany-signup-submit"
          >{busy ? t("dany.signup.creating") : t("dany.signup.createAndPublish")}</Button>
          <Button variant="ghost" size="sm" icon="arrow-left" disabled={busy}
                  onClick={back} data-testid="dany-signup-back">
            {t("dany.signup.back")}
          </Button>
        </div>
        {legal}
      </div>
    );
  }

  const index = STEPS.indexOf(step);
  return (
    <form onSubmit={advance} style={shell} data-testid={`dany-signup-step-${step}`}>
      {/* Coming back to fix one field is not step 2 of 3 — it is one field,
          and a counter here would say they had lost the other two. */}
      <p style={{ ...eyebrow, marginBottom: "var(--space-2)" }}>
        <Icon name="user" size={12} />
        {editing
          ? t("dany.signup.editingField")
          : t("dany.signup.stepOf", { current: index + 1, total: STEPS.length })}
      </p>
      <DanyStageField
        label={t(`dany.signup.${step}`)}
        hint={step === "password" ? t("dany.signup.passwordHint") : null}
        autoFocus
      >
        <Input
          key={step}
          autoFocus
          type={step === "email" ? "email" : step === "password" ? "password" : "text"}
          inputMode={step === "email" ? "email" : undefined}
          autoComplete={
            step === "name" ? "name"
              : step === "email" ? "email" : "new-password"
          }
          enterKeyHint={editing || index === STEPS.length - 1 ? "done" : "next"}
          value={values[step]}
          onChange={set(step)}
          data-testid={`dany-signup-input-${step}`}
        />
      </DanyStageField>
      {error && <p style={danger} data-testid="dany-signup-error">{error}</p>}
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        <Button
          type="submit" variant="primary" size="md" iconRight="arrow-right"
          disabled={busy || !!problem} data-testid={`dany-signup-next-${step}`}
        >{editing ? t("dany.signup.saveField") : t("dany.signup.next")}</Button>
        <Button variant="ghost" size="sm" icon="arrow-left" disabled={busy}
                onClick={back} data-testid="dany-signup-back">
          {t("dany.signup.back")}
        </Button>
      </div>
    </form>
  );
}

function Summary({ label, value, onEdit, editLabel, testid }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", gap: "var(--space-3)",
      marginBottom: 6,
    }}>
      <dt style={{
        minWidth: 90, fontFamily: "var(--font-display)",
        fontSize: "var(--text-xs)", textTransform: "uppercase",
        letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
      }}>{label}</dt>
      <dd style={{
        flex: 1, fontSize: "var(--text-sm)", color: "var(--text-body)",
        wordBreak: "break-word", minWidth: 0,
      }}>{value}</dd>
      {/* N3. The affordance, and it is on the row rather than as one "Edit"
          button under the card: which of the three needs fixing is the whole
          question, and a single button would land them back in the wizard to
          find out. */}
      {onEdit && (
        <button
          type="button" onClick={onEdit} data-testid={testid}
          style={{
            border: 0, background: "none", padding: 0, cursor: "pointer",
            fontSize: "var(--text-xs)", color: "var(--text-accent)",
            textDecoration: "underline", flexShrink: 0,
          }}
        >{editLabel}</button>
      )}
    </div>
  );
}

const header = {
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-base)", color: "var(--text-body)",
};

const sub = {
  marginTop: 4, fontSize: "var(--text-sm)", color: "var(--text-muted)",
  lineHeight: "var(--leading-relaxed)",
};

const danger = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)", color: "var(--danger)",
};

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};
